/*
 * ConfirmDialog: "are you sure" before an action that changes something. The Modal (size sm)
 * with the question as its body and two buttons in the footer: Cancel (secondary) and the
 * action (primary, or danger for a destructive one). Not in Figma.
 *
 * `phrase`: the action stays disabled until the phrase is typed exactly (for the few actions
 * that can't be undone, e.g. preparing a tenant's runtime). `busy` disables both buttons while
 * the action runs; `error` shows under the body when it failed (the dialog stays open).
 */
import { useId, useState } from 'react'
import type { ReactNode } from 'react'
import { Button } from './Button'
import { TextInput } from './Field'
import { Modal } from './Modal'

export interface ConfirmDialogProps {
    open: boolean
    title: ReactNode
    children?: ReactNode
    confirmLabel: string
    cancelLabel: string
    closeLabel: string
    onConfirm: () => void
    onClose: () => void
    tone?: 'default' | 'danger'
    busy?: boolean
    error?: string | null
    /** Typed confirmation: the exact text, and the field's label ("Type PREPARE acme"). */
    phrase?: string
    phraseLabel?: string
}

export function ConfirmDialog({
    open,
    title,
    children,
    confirmLabel,
    cancelLabel,
    closeLabel,
    onConfirm,
    onClose,
    tone = 'default',
    busy = false,
    error,
    phrase,
    phraseLabel,
}: ConfirmDialogProps) {
    const [typed, setTyped] = useState('')
    const bodyId = useId()
    const ready = !busy && (phrase === undefined || typed === phrase)
    const close = () => {
        if (busy) return
        setTyped('')
        onClose()
    }
    return (
        <Modal
            open={open}
            onClose={close}
            title={title}
            closeLabel={closeLabel}
            size="sm"
            describedBy={bodyId}
            footer={
                <div className="flex w-full justify-end gap-[8px]">
                    <Button onClick={close} disabled={busy}>
                        {cancelLabel}
                    </Button>
                    <Button variant={tone === 'danger' ? 'danger' : 'primary'} onClick={onConfirm} disabled={!ready} data-testid="confirm-action">
                        {confirmLabel}
                    </Button>
                </div>
            }
        >
            <div id={bodyId} className="flex flex-col gap-[16px] text-[14px] leading-[20px] font-normal text-[var(--text-default)]">
                {children}
                {phrase !== undefined && (
                    <TextInput label={phraseLabel ?? phrase} value={typed} onChange={(event) => setTyped(event.target.value)} autoComplete="off" spellCheck={false} />
                )}
                {error && (
                    <p role="alert" className="m-0 text-[var(--text-danger)]">
                        {error}
                    </p>
                )}
            </div>
        </Modal>
    )
}
